import React, { FC, memo, useCallback } from 'react';
import styled from '@emotion/styled';
import { useButton, useLogger } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import { SERVICE_TYPE } from '../../../../../__queries__/globalTypes';
import { useExpressCartActions } from '../../Providers/ExpressCartProvider';
import arrowForwardIcon from '../../../../Assets/icons/arrow_forward.svg';
import {
  glassSurfaceCss,
  glassBorderCss,
  glassCardShadowCss,
} from '../../Components/GlassPill/glassSurface';

/**
 * Frosted glass card over the photo - same language as the GlassPill - with
 * the icon on a disc in the theme's button color. Pressed state is a class.
 */
const Card = styled.button`
  appearance: none;
  flex: 1 1 0;
  min-width: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 36px;
  padding: 40px;
  border-radius: 32px;
  ${glassBorderCss}
  ${glassSurfaceCss}
  ${glassCardShadowCss}
  color: var(--colorWhite);
  font-family: var(--eposFontFamily);
  text-align: left;
  cursor: pointer;
  transition: transform 0.18s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.18s ease-out;

  &.is-touching {
    transform: scale(0.96);
    background: rgba(255, 255, 255, 0.24);
  }

  /* Landscape has less vertical room to work with than portrait. */
  @media (orientation: landscape) {
    gap: 24px;
    padding: 28px;
  }
`;

/** The icon disc - the one place the theme's action color shows on the welcome screen. */
const IconDisc = styled.span`
  width: 128px;
  height: 128px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--eposButton);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.3);

  @media (orientation: landscape) {
    width: 88px;
    height: 88px;
  }
`;

const Label = styled.span`
  font-size: 46px;
  font-weight: 800;
  letter-spacing: -0.6px;
  line-height: 1.1;

  @media (orientation: landscape) {
    font-size: 32px;
  }
`;

/** Small chevron in the top right - says "this takes you onward". */
const Chevron = styled.span`
  position: absolute;
  top: 28px;
  right: 28px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.12);

  @media (orientation: landscape) {
    top: 24px;
    right: 24px;
  }
`;

interface Props {
  serviceType: SERVICE_TYPE;
  icon: string;
  label: string;
  /** Called after the service type has been set on the cart. */
  onStart: () => void;
  testId: string;
}

/** One service type choice. Sets the type on the cart itself, then hands over. */
const ServiceChoice: FC<Props> = ({ serviceType, icon, label, onStart, testId }) => {
  const logger = useLogger('epos-welcome');
  const cartActions = useExpressCartActions();

  const handleClick = useCallback(() => {
    logger.info('Service type selected', { serviceType });
    cartActions.updateServiceType(serviceType);
    onStart();
  }, [serviceType, cartActions, onStart]);

  const { touching, disabled, disabledToast, ...button } = useButton({ onClick: handleClick });

  return (
    <Card {...button} className={touching ? 'is-touching' : undefined} data-testid={testId}>
      <IconDisc>
        <Icon src={icon} color="var(--eposButtonText)" width="66px" height="66px" />
      </IconDisc>
      <Label>{label}</Label>
      <Chevron>
        <Icon src={arrowForwardIcon} color="var(--colorWhite)" width="22px" height="22px" />
      </Chevron>
    </Card>
  );
};

export default memo(ServiceChoice);
